import { brandName, productName, productFullName } from '../brand/brand';
// The app bar (D-161, proposals/2026-10-05-app-rail): one compact, icons-only bar, 56 px, the same in every
// Dugzi app. Home, the pinned apps, More; Ask Dugzi, the bell and the account at the bottom. On phones it
// is a bottom bar (4 pins + More). One shared component: `AppLauncher` mounts it (every app already has
// one), and `AccountSwitcher` / the bell move into its foot through slots, so no app lays out its own bar.
import { useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore, type KeyboardEvent as RKey, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { BookOpen, CalendarDays, CircleUserRound, ContactRound, FileText, FolderOpen, ListTodo, Mail, MessagesSquare, Presentation, Search, Sheet, Sparkles, StickyNote } from 'lucide-react';
import { productAvailable } from '../release-scope';
import { DUGZI_APPS, appHref, type DugziApp } from './apps-registry';
import { ContextMenu, type MenuEntry } from './menu';
import { Tooltip } from './tooltip';
import { Dialog } from './feedback';
import { Switch } from './toggles';
import { COOKIE, MAX_PINS, appBarRequest, badgeLabel, badgeModeOf, cleanPrefs, defaultPrefs, homeUrl, type AppBarPrefs, type BadgeMode } from './apprail-core';
import { useT } from '../i18n';
export * from './apprail-core';

export const APP_ICONS: Record<string, DugziApp['Icon']> = {
  mail: Mail, calendar: CalendarDays, chat: MessagesSquare, contacts: ContactRound, drive: FolderOpen, docs: FileText,
  sheets: Sheet, slides: Presentation, tasks: ListTodo, notes: StickyNote, wiki: BookOpen, search: Search, ai: Sparkles, account: CircleUserRound,
};

// ---------------------------------------------------------------- preferences

/** The domain every app shares (`.dugzi.com`), so pins set in one app show in all of them at once. */
function cookieDomain(): string | null {
  const h = location.hostname;
  if (/^\d+\.\d+\.\d+\.\d+$/.test(h) || !h.includes('.')) return null;
  const parts = h.split('.');
  return parts.length > 2 ? '.' + parts.slice(1).join('.') : '.' + h;
}
function readCookie(): AppBarPrefs | null {
  try {
    const m = document.cookie.split('; ').find((c) => c.startsWith(COOKIE + '='));
    if (m) return cleanPrefs(JSON.parse(decodeURIComponent(m.slice(COOKIE.length + 1))));
  } catch { /* none */ }
  try { const s = localStorage.getItem(COOKIE); if (s) return cleanPrefs(JSON.parse(s)); } catch { /* none */ }
  return null;
}
function writeCookie(p: AppBarPrefs) {
  const d = cookieDomain();
  const secure = location.protocol === 'https:' ? '; Secure' : '';
  try { document.cookie = `${COOKIE}=${encodeURIComponent(JSON.stringify(p))}; Path=/; Max-Age=31536000; SameSite=Lax${d ? `; Domain=${d}` : ''}${secure}`; } catch { /* none */ }
  try { localStorage.setItem(COOKIE, JSON.stringify(p)); } catch { /* none */ }
}

let prefs: AppBarPrefs = defaultPrefs();
let loadedPrefs = false;
const subs = new Set<() => void>();
const emit = () => subs.forEach((f) => f());
function ensurePrefs() { if (!loadedPrefs) { loadedPrefs = true; prefs = readCookie() ?? defaultPrefs(); } }
export function setAppBarPrefs(next: AppBarPrefs | ((p: AppBarPrefs) => AppBarPrefs), fromAccount = false) {
  ensurePrefs();
  const n = cleanPrefs(typeof next === 'function' ? next(prefs) : next);
  prefs = fromAccount ? n : { ...n, t: Date.now() };
  writeCookie(prefs);
  emit();
  if (!fromAccount) pushToAccount(prefs);
}
export function useAppBarPrefs(): AppBarPrefs {
  ensurePrefs();
  return useSyncExternalStore((f) => { subs.add(f); return () => { subs.delete(f); }; }, () => prefs, () => prefs);
}

/**
 * Account keeps the copy that follows the person to a new browser (`/api/me/app-bar` on Dugzi Account).
 * Apps other than Account reach it through the shared cookie; Account itself syncs both ways when it opens
 * and on every change, so the newest copy wins.
 */
let accountSync = false;
let syncAccount: string | undefined;
function pushToAccount(p: AppBarPrefs) {
  const init = appBarRequest(syncAccount, p);
  if (!accountSync || !init) return;
  fetch('/api/me/app-bar', init).catch(() => {});
}
async function pullFromAccount(account: string | undefined) {
  accountSync = true;
  syncAccount = account;
  const init = appBarRequest(account);
  if (!init) return;
  try {
    const r = await fetch('/api/me/app-bar', init);
    if (!r.ok) return;
    const server = cleanPrefs(await r.json());
    ensurePrefs();
    if (server.t > prefs.t) setAppBarPrefs(server, true); else if (prefs.t > server.t) pushToAccount(prefs);
  } catch { /* offline */ }
}

// ---------------------------------------------------------------- badges (one source: the activity service)

/** Unread per product, from the live `unread` event (same notifications as the bell, filtered by product). */
let badgeCounts: Record<string, number> = {};
export function setRailBadges(by: unknown) {
  const next: Record<string, number> = {};
  if (by && typeof by === 'object') for (const [k, v] of Object.entries(by)) if (typeof v === 'number' && v > 0) next[k] = Math.floor(v);
  badgeCounts = next;
  emit();
}
const useBadges = () => useSyncExternalStore((f) => { subs.add(f); return () => { subs.delete(f); }; }, () => badgeCounts, () => badgeCounts);

function useOnline() {
  return useSyncExternalStore((f) => { addEventListener('online', f); addEventListener('offline', f); return () => { removeEventListener('online', f); removeEventListener('offline', f); }; }, () => navigator.onLine, () => true);
}

// ---------------------------------------------------------------- the host and its slots

export type RailSlot = 'pins' | 'more' | 'ask' | 'bell' | 'account';
let host: HTMLElement | null = null;
const slotEls: Partial<Record<RailSlot, HTMLElement>> = {};
let enabled = false;
const PHONE = '(max-width: 767px)';

function build(): HTMLElement {
  if (host) return host;
  const nav = document.createElement('nav');
  nav.className = 'dz-rail';
  nav.setAttribute('aria-label', `${brandName()} apps`);
  nav.dataset.testid = 'app-rail';
  const mk = (cls: string, slots: RailSlot[]) => {
    const d = document.createElement('div');
    d.className = cls;
    for (const s of slots) { const e = document.createElement('div'); e.className = `dz-rail__slot dz-rail__slot--${s}`; e.dataset.slot = s; slotEls[s] = e; d.appendChild(e); }
    return d;
  };
  nav.append(mk('dz-rail__top', ['pins', 'more']), mk('dz-rail__foot', ['ask', 'bell', 'account']));
  document.body.insertBefore(nav, document.body.firstChild);
  host = nav;
  return nav;
}
/** Turns the bar on for this page (idempotent). */
export function enableAppRail() {
  if (enabled || typeof document === 'undefined') return;
  enabled = true;
  build();
  document.documentElement.classList.add('dz-has-rail');
  emit();
}
/** The element for a slot on desktop, null when the bar isn't on or it is a phone (phones keep bell and account in the header). */
export function useRailSlot(slot: 'bell' | 'account'): HTMLElement | null {
  const on = useSyncExternalStore((f) => { subs.add(f); return () => { subs.delete(f); }; }, () => enabled, () => false);
  const phone = useSyncExternalStore((f) => { const m = matchMedia(PHONE); m.addEventListener('change', f); return () => m.removeEventListener('change', f); }, () => matchMedia(PHONE).matches, () => false);
  return on && !phone ? slotEls[slot] ?? null : null;
}
export const railEnabled = () => enabled;
/** True when the bar is on (re-renders on change). */
export function useRailOn(): boolean {
  return useSyncExternalStore((f) => { subs.add(f); return () => { subs.delete(f); }; }, () => enabled, () => false);
}
/** Renders into a slot of the bar, or inline where there is no bar. */
export function InRail({ slot, children }: { slot: 'bell' | 'account'; children: ReactNode }) {
  const el = useRailSlot(slot);
  return el ? createPortal(children, el) : <>{children}</>;
}

// ---------------------------------------------------------------- the bar

export interface AppRailProps {
  current: string;
  accountsUrl: string;
  account?: string;
  apps?: readonly DugziApp[];
  onAsk(): void;
  /** The "More" disclosure (the launcher panel), rendered by the launcher into its slot. */
  more: ReactNode;
}

const isTyping = (t: EventTarget | null) => !!(t as HTMLElement | null)?.closest?.('input, textarea, select, [contenteditable="true"], [role="combobox"], [role="textbox"], .dz-grid');

export function AppRail({ current, accountsUrl, account, apps = DUGZI_APPS, onAsk, more }: AppRailProps) {
  const t = useT();
  const p = useAppBarPrefs();
  useRailOn();
  useEffect(() => { host?.setAttribute('aria-label', t('ui.apps.label')); }, [t]);
  const counts = useBadges();
  const online = useOnline();
  const [custom, setCustom] = useState(false);
  const live = apps.filter((a) => a.status === 'live' && productAvailable(a.id));
  const byId = (id: string) => live.find((a) => a.id === id);
  const pins = p.pins.map(byId).filter((a): a is DugziApp => !!a && a.id !== 'ai' && a.id !== 'account');
  const rootRef = useRef<HTMLDivElement>(null);
  const [focusId, setFocusId] = useState<string>('home');
  const pinsSlot = slotEls.pins;
  const askSlot = slotEls.ask;
  const moreSlot = slotEls.more;

  useEffect(() => { if (current === 'account') void pullFromAccount(account); }, [current, account]);
  useEffect(() => { if (current === 'account') accountSync = true; }, [current]);

  const go = (a: DugziApp) => { location.assign(a.id === current ? a.home : appHref(a, accountsUrl, account)); };

  // g then 6–9 (outside text fields) opens the pin at that place (g then 1–5 is the account at that place, D-049,
  // `useAccountShortcuts`); F6 moves between the bar and the page.
  useEffect(() => {
    let g = 0;
    const key = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.isComposing) return;
      if (e.key === 'F6' && !e.ctrlKey && !e.metaKey && !e.altKey) {
        const rail = document.querySelector('.dz-rail');
        if (!rail) return;
        e.preventDefault();
        if (rail.contains(document.activeElement)) {
          const main = document.querySelector<HTMLElement>('main, [role="main"], #main');
          if (main) { if (!main.hasAttribute('tabindex')) main.setAttribute('tabindex', '-1'); main.focus(); }
        } else rail.querySelector<HTMLElement>('[data-rail-item][tabindex="0"]')?.focus();
        return;
      }
      // A key the page already used is not the bar's: `g 1`–`g 5` switch accounts where the account switcher has
      // shortcuts (D-049, `useAccountShortcuts`), so those never also open an app.
      if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.altKey || isTyping(e.target)) { g = 0; return; }
      if (e.key === 'g' && !e.shiftKey) { g = Date.now(); return; }
      if (g && Date.now() - g < 1200 && /^[6-9]$/.test(e.key)) {
        g = 0;
        const a = pins[Number(e.key) - 1];
        if (a) { e.preventDefault(); go(a); }
        return;
      }
      g = 0;
    };
    document.addEventListener('keydown', key);
    return () => document.removeEventListener('keydown', key);
  });

  // One tab stop for the apps; arrows move, Home and End jump.
  const move = (e: RKey) => {
    const items = Array.from(rootRef.current?.querySelectorAll<HTMLElement>('[data-rail-item]') ?? []);
    const at = items.indexOf(document.activeElement as HTMLElement);
    if (at < 0) return;
    const to = e.key === 'ArrowDown' || e.key === 'ArrowRight' ? at + 1 : e.key === 'ArrowUp' || e.key === 'ArrowLeft' ? at - 1 : e.key === 'Home' ? 0 : e.key === 'End' ? items.length - 1 : null;
    if (to === null) return;
    e.preventDefault();
    const next = items[(to + items.length) % items.length];
    setFocusId(next.dataset.railItem ?? 'home');
    next.focus();
  };

  const prefetched = useRef(new Set<string>());
  const prefetch = (href: string) => {
    if (prefetched.current.has(href) || !navigator.onLine) return;
    prefetched.current.add(href);
    const l = document.createElement('link'); l.rel = 'prefetch'; l.href = href; document.head.appendChild(l);
  };
  const hover = useRef(0);

  const update = (fn: (x: AppBarPrefs) => AppBarPrefs) => setAppBarPrefs(fn);
  const menuFor = (a: DugziApp): MenuEntry[] => {
    const i = p.pins.indexOf(a.id);
    const mode = badgeModeOf(p, a.id);
    const setMode = (m: BadgeMode) => update((x) => ({ ...x, badges: { ...x.badges, [a.id]: m } }));
    return [
      a.create ? { label: a.create.label, onSelect: () => window.open(appHref(a, accountsUrl, account, a.create!.path), '_blank', 'noopener') } : false,
      { label: t('ui.rail.new_tab'), onSelect: () => window.open(appHref(a, accountsUrl, account), '_blank', 'noopener') },
      { separator: true },
      { heading: t('ui.rail.show') },
      { label: t('ui.rail.count'), checked: mode === 'count', onSelect: () => setMode('count') },
      { label: t('ui.rail.dot'), checked: mode === 'dot', onSelect: () => setMode('dot') },
      { label: t('ui.rail.nothing'), checked: mode === 'none', onSelect: () => setMode('none') },
      { separator: true },
      { label: t('ui.rail.move_up'), disabled: i <= 0, onSelect: () => update((x) => ({ ...x, pins: swap(x.pins, i, i - 1) })) },
      { label: t('ui.rail.move_down'), disabled: i < 0 || i >= p.pins.length - 1, onSelect: () => update((x) => ({ ...x, pins: swap(x.pins, i, i + 1) })) },
      i >= 0
        ? { label: t('ui.rail.remove'), onSelect: () => update((x) => ({ ...x, pins: x.pins.filter((id) => id !== a.id) })) }
        : { label: t('ui.rail.keep'), disabled: p.pins.length >= MAX_PINS, onSelect: () => update((x) => ({ ...x, pins: [...x.pins, a.id] })) },
      { separator: true },
      { label: t('ui.rail.customise'), onSelect: () => setCustom(true) },
    ];
  };

  const renderItem = ({ id, href, name, icon, here, badge, label, onClick }: { id: string; href: string; name: string; icon: ReactNode; here?: boolean; badge?: ReactNode; label?: string; onClick?: (e: React.MouseEvent) => void }) => (
    <Tooltip key={id} content={name} placement="right">
      <a className="dz-rail__item" href={href} data-rail-item={id} data-app={id} aria-current={here ? 'page' : undefined} aria-label={label ?? name} tabIndex={focusId === id ? 0 : -1}
        onFocus={() => setFocusId(id)} onClick={onClick}
        onPointerEnter={() => { clearTimeout(hover.current); hover.current = window.setTimeout(() => prefetch(href), 100); }} onPointerLeave={() => clearTimeout(hover.current)}>
        <span className="dz-rail__icon">{icon}</span>
        {badge}
      </a>
    </Tooltip>
  );

  const phonePins = pins.slice(0, 4);
  const list = typeof matchMedia === 'function' && matchMedia(PHONE).matches ? phonePins : pins;
  const Home = renderItem({ id: 'home', href: homeUrl(accountsUrl), name: t('ui.rail.home'), icon: <span className="dz-rail__mark" aria-hidden>d</span> });
  const items = (
    <div ref={rootRef} className="dz-rail__apps" role="presentation" onKeyDown={move} data-testid="app-rail-apps">
      {Home}
      {list.map((a) => {
        const I = a.Icon ?? APP_ICONS[a.id];
        const mode = badgeModeOf(p, a.id);
        const n = counts[a.id] ?? 0;
        const show = !p.quiet && online && mode !== 'none' && n > 0;
        return (
          <ContextMenu key={a.id} items={() => menuFor(a)} label={t('ui.rail.actions', { app: a.name })}>
            <span className="dz-rail__cell">
              {renderItem({
                id: a.id, href: a.id === current ? a.home : appHref(a, accountsUrl, account), name: a.name, here: a.id === current,
                icon: I ? <I aria-hidden size={22} /> : null,
                label: badgeLabel(a.name, n, mode, p.quiet, online),
                badge: show ? <span className={`dz-rail__badge${mode === 'dot' ? ' dz-rail__badge--dot' : ''}`} aria-hidden>{mode === 'count' ? (n > 99 ? '99+' : n) : ''}</span> : null,
                onClick: a.id === current ? (e) => { if (!e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey) e.preventDefault(); } : undefined,
              })}
            </span>
          </ContextMenu>
        );
      })}
    </div>
  );
  const ask = current === 'ai' || !productAvailable('ai') ? null : (
    <Tooltip content={productName('ai')} shortcut={/Mac|iPhone|iPad/.test(navigator.platform) ? '⌘J' : 'Ctrl+J'} placement="right">
      <button type="button" className="dz-rail__item dz-rail__btn" onClick={onAsk} aria-label={productName('ai')} data-testid="rail-ask"><span className="dz-rail__icon"><Sparkles aria-hidden size={22} /></span></button>
    </Tooltip>
  );

  return (
    <>
      {pinsSlot && createPortal(items, pinsSlot)}
      {moreSlot && createPortal(more, moreSlot)}
      {askSlot && ask && createPortal(ask, askSlot)}
      <Customise open={custom} onClose={() => setCustom(false)} apps={live.filter((a) => a.id !== 'ai' && a.id !== 'account')} />
    </>
  );
}

const swap = <T,>(l: T[], a: number, b: number): T[] => { if (a < 0 || b < 0 || a >= l.length || b >= l.length) return l; const n = [...l]; [n[a], n[b]] = [n[b], n[a]]; return n; };

/** Customise: reorder with buttons (dragging is never the only way), Count/Dot/Nothing per app, quiet mode, reset. */
function Customise({ open, onClose, apps }: { open: boolean; onClose(): void; apps: DugziApp[] }) {
  const t = useT();
  const p = useAppBarPrefs();
  const set = (fn: (x: AppBarPrefs) => AppBarPrefs) => setAppBarPrefs(fn);
  const pinned = p.pins.map((id) => apps.find((a) => a.id === id)).filter((a): a is DugziApp => !!a);
  const others = apps.filter((a) => !p.pins.includes(a.id));
  const modes: BadgeMode[] = ['count', 'dot', 'none'];
  const label = { count: t('ui.rail.count'), dot: t('ui.rail.dot'), none: t('ui.rail.nothing') } as const;
  return (
    <Dialog open={open} onClose={onClose} title={t('ui.rail.customise_title')}
      actions={<><button type="button" className="dz-btn dz-btn--ghost" onClick={() => set(() => defaultPrefs())}>{t('ui.rail.reset')}</button><button type="button" className="dz-btn dz-btn--primary" onClick={onClose}>{t('ui.done')}</button></>}>
      <div className="dz-rail-custom" data-testid="rail-customise">
        <p className="dz-muted dz-small">{t('ui.rail.customise_hint', { max: MAX_PINS, account: productFullName('account') })}</p>
        <Switch checked={!p.quiet} onChange={(v) => set((x) => ({ ...x, quiet: !v }))} label={t('ui.rail.badges')} hint={t('ui.rail.badges_hint')} />
        <h3 className="dz-rail-custom__h">{t('ui.rail.your_bar')}</h3>
        <ul className="dz-rail-custom__list">
          {pinned.map((a, i) => {
            const I = a.Icon ?? APP_ICONS[a.id];
            return (
              <li key={a.id}>
                <span className="dz-rail-custom__name">{I && <I aria-hidden size={20} />}{a.name}</span>
                <span className="dz-rail-custom__modes" role="group" aria-label={t('ui.rail.show_app', { app: a.name })}>
                  {modes.map((m) => <button key={m} type="button" className="dz-btn dz-btn--ghost dz-btn--sm" aria-pressed={badgeModeOf(p, a.id) === m} onClick={() => set((x) => ({ ...x, badges: { ...x.badges, [a.id]: m } }))}>{label[m]}</button>)}
                </span>
                <span className="dz-rail-custom__acts">
                  <button type="button" className="dz-btn dz-btn--ghost dz-btn--sm" disabled={i === 0} aria-label={t('ui.rail.move_app_up', { app: a.name })} onClick={() => set((x) => ({ ...x, pins: swap(x.pins, i, i - 1) }))}>{t('ui.rail.up')}</button>
                  <button type="button" className="dz-btn dz-btn--ghost dz-btn--sm" disabled={i === pinned.length - 1} aria-label={t('ui.rail.move_app_down', { app: a.name })} onClick={() => set((x) => ({ ...x, pins: swap(x.pins, i, i + 1) }))}>{t('ui.rail.down')}</button>
                  <button type="button" className="dz-btn dz-btn--ghost dz-btn--sm" aria-label={t('ui.rail.remove_app', { app: a.name })} onClick={() => set((x) => ({ ...x, pins: x.pins.filter((id) => id !== a.id) }))}>{t('ui.rail.remove_short')}</button>
                </span>
              </li>
            );
          })}
        </ul>
        {others.length > 0 && <>
          <h3 className="dz-rail-custom__h">{t('ui.rail.more')}</h3>
          <ul className="dz-rail-custom__list">
            {others.map((a) => {
              const I = a.Icon ?? APP_ICONS[a.id];
              return (
                <li key={a.id}>
                  <span className="dz-rail-custom__name">{I && <I aria-hidden size={20} />}{a.name}</span>
                  <span className="dz-rail-custom__acts"><button type="button" className="dz-btn dz-btn--ghost dz-btn--sm" disabled={p.pins.length >= MAX_PINS} aria-label={t('ui.rail.add_app', { app: a.name })} onClick={() => set((x) => ({ ...x, pins: [...x.pins, a.id] }))}>{t('ui.rail.add')}</button></span>
                </li>
              );
            })}
          </ul>
        </>}
      </div>
    </Dialog>
  );
}

/** For the command search: "Go to Mail" … one entry per live app, in bar order. */
export function goToApps(accountsUrl: string, account?: string, apps: readonly DugziApp[] = DUGZI_APPS): { id: string; title: string; href: string }[] {
  return apps.filter((a) => a.status === 'live' && productAvailable(a.id)).map((a) => ({ id: a.id, title: `Go to ${a.name}`, href: appHref(a, accountsUrl, account) }));
}

export function useRailLayout() { useLayoutEffect(() => { enableAppRail(); }, []); }
